/**
 * GrantCoverageNotice
 *
 * The member holds unused PROMOTIONAL access — days support granted them, or a redeemed
 * offline licence — and this purchase honours it. Two ways, and the notice states whichever
 * applies:
 *
 *  - DEFERRED: the first payment moves to the day that access runs out (a Stripe trial).
 *    This has to be said out loud, because the cart line keeps its full price — a trial
 *    changes the first charge, not the subscription's recurring amount. Without it the member
 *    reads "€59.99 due today", clicks through, and Stripe asks them for nothing: two
 *    different numbers for one purchase, at the moment they are deciding whether to commit.
 *  - CREDITED: the remaining time is valued in money and taken off this payment. The
 *    reduction reaches the total on its own, as a line price adjustment, so this does not
 *    make the arithmetic right — it NAMES the reduction. Otherwise the member sees an
 *    unexplained discount with no way to tell it is the time they were promised.
 *
 * Shown wherever the amount is — the cart AND the payment step — for the same reason
 * LegacyMigrationNotice is. Rendered from order properties (not FE state), so it survives a
 * hard refresh.
 */

import type { MembershipPlanChange } from "@/types/cart";
import { useLocalizedTexts, t } from "@/utils/translations";
import { formatDate } from "@/utils/entitlements";
import { formatMinorUnitsAsCurrency } from "@/utils/priceFormat";
import styles from "./GrantCoverageNotice.module.css";

interface GrantCoverageNoticeProps {
  /** The cart's membership change; the notice renders only when a grant is being honoured. */
  membershipChange: MembershipPlanChange | null | undefined;
  className?: string;
}

export function GrantCoverageNotice({
  membershipChange,
  className,
}: GrantCoverageNoticeProps) {
  const localizedTexts = useLocalizedTexts();

  const trialEnd = membershipChange?.grantTrialEndUtc;
  const creditMinor = membershipChange?.grantCreditMinor;
  const creditCurrency = membershipChange?.grantCreditCurrency;

  // The two treatments are mutually exclusive — the backend stamps exactly one — so the
  // deferred charge is checked first and the credit is the only other possibility.
  const deferred = !!trialEnd;
  const credited =
    !deferred && !!creditMinor && creditMinor > 0 && !!creditCurrency;

  if (!deferred && !credited) {
    return null;
  }

  const heading = deferred
    ? t(
        localizedTexts,
        "Tracker.Cart.GrantCoverage.TrialHeading",
        "Nothing to pay today"
      )
    : t(
        localizedTexts,
        "Tracker.Cart.GrantCoverage.CreditHeading",
        "Your remaining access is credited"
      );

  const body = deferred
    ? t(
        localizedTexts,
        "Tracker.Cart.GrantCoverage.TrialBody",
        "You keep the access you already have. We'll save your payment details now and take the first payment on {date}, when your current access ends."
      ).replace("{date}", formatDate(trialEnd!))
    : t(
        localizedTexts,
        "Tracker.Cart.GrantCoverage.CreditBody",
        "We've taken {amount} off this purchase for the access you already have, so you're not paying twice for the same time."
      ).replace(
        "{amount}",
        formatMinorUnitsAsCurrency(creditMinor!, creditCurrency!)
      );

  return (
    <div
      className={className ? `${styles.notice} ${className}` : styles.notice}
      role="note"
    >
      <p className={styles.heading}>{heading}</p>
      <p className={styles.body}>{body}</p>
    </div>
  );
}
